import { memo } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import { formatFileSize } from "src/reusable/services/fileUploadManagerService";
import { getFileExtension } from "src/views/cloudFiles/services/CloudFileServices";
import { getFileIconAndColor } from "src/reusable/Functions";
import type { RfpFileState } from "../../store/ProposalGenerationStore";

interface InputDocumentsTabProps {
  inputDocuments: RfpFileState[];
}

/**
 * Build the document viewer URL for an input document.
 * Returns null if the document doesn't have enough info to build a link.
 */
const getDocumentViewerUrl = (doc: RfpFileState): string | null => {
  if (doc.datasetId && doc.sdrId) {
    return `/datasets/${doc.datasetId}/document/${doc.sdrId}`;
  }
  return null;
};

/**
 * InputDocumentsTab - Displays input documents selected for proposal generation.
 *
 * Each document is a clickable item that opens the DocumentViewer in a new window.
 * Follows the same pattern as StagesTab and ArtifactsTab for sidebar consistency.
 */
const InputDocumentsTab = memo(({ inputDocuments }: InputDocumentsTabProps) => {
  if (!inputDocuments.length) {
    return (
      <NoContentCard
        title="No input documents"
        description="Input documents will appear here once files are uploaded or selected for report generation."
      />
    );
  }

  const handleDocumentClick = (doc: RfpFileState) => {
    const url = getDocumentViewerUrl(doc);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <SideBarScroll>
      <Box sx={{ px: 1, pt: 0.5 }}>
        <List disablePadding data-testid="pg-input-docs-list">
          {inputDocuments.map((doc, index) => {
            const url = getDocumentViewerUrl(doc);
            const isClickable = url !== null;
            const { icon } = getFileIconAndColor({
              fileName: doc.name,
              fontSize: 20,
            });
            const fileExt = getFileExtension(doc.name);

            return (
              <ListItemButton
                key={doc.id ?? doc.sdrId ?? `doc-${index}`}
                data-testid={`pg-input-doc-item-${doc.id ?? doc.sdrId ?? index}`}
                onClick={() => handleDocumentClick(doc)}
                disabled={!isClickable}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover .open-icon": {
                    opacity: 1,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      noWrap
                      title={doc.name}
                      sx={{ fontWeight: 500 }}
                    >
                      {doc.name}
                    </Typography>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.25,
                      }}
                    >
                      {fileExt && (
                        <Chip
                          label={fileExt}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                      {doc.size > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(doc.size)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {isClickable && (
                  <OpenInNewIcon
                    className="open-icon"
                    sx={{
                      fontSize: 16,
                      color: "text.secondary",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ml: 1,
                    }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </SideBarScroll>
  );
});

InputDocumentsTab.displayName = "InputDocumentsTab";

export default InputDocumentsTab;
